import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import VideoTile from "@/components/VideoTile";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarPicker } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { Rewind, ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";

const ARCHIVE_DAYS = 35;

function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function fmtIsoDate(d) {
  // Local-date ISO (YYYY-MM-DD) — avoids UTC drift for IST users
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function fmtLong(d) {
  const today = startOfDay(new Date());
  const s = startOfDay(d);
  const diff = Math.round((today - s) / (1000 * 60 * 60 * 24));
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/**
 * Playback screen — recorded footage review with a compact calendar popover.
 * Archive is capped at 35 days back; future dates are disabled.
 */
export default function Playback() {
  const [cameras, setCameras] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => startOfDay(new Date()));
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    api.get("/cameras")
      .then((r) => {
        if (!mounted) return;
        const cams = r.data || [];
        setCameras(cams);
        if (cams.length && !selected) setSelected(cams[0]);
      })
      .catch((e) => mounted && toast.error(formatApiError(e)));
    return () => { mounted = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCam = useMemo(
    () => cameras.find((c) => c.id === selected?.id) || null,
    [cameras, selected]
  );

  const today = useMemo(() => startOfDay(new Date()), []);
  const oldest = useMemo(() => {
    const d = startOfDay(new Date());
    d.setDate(d.getDate() - (ARCHIVE_DAYS - 1));
    return d;
  }, []);

  const shiftDay = (delta) => {
    setSelectedDate((prev) => {
      const next = startOfDay(new Date(prev));
      next.setDate(next.getDate() + delta);
      if (next < oldest) return oldest;
      if (next > today) return today;
      return next;
    });
  };

  const isToday = startOfDay(selectedDate).getTime() === today.getTime();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = startOfDay(selectedDate).getTime() === yesterday.getTime();
  const canPrev = startOfDay(selectedDate) > oldest;
  const canNext = startOfDay(selectedDate) < today;

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col overflow-x-hidden pb-14 md:pb-0">
      <AppHeader active="playback" />

      {/* Camera picker strip */}
      <div className="border-b border-zinc-900 bg-zinc-950 px-3 py-2 flex items-center gap-2 overflow-x-auto no-scrollbar">
        <div className="flex items-center gap-2 shrink-0 mono text-[10px] uppercase tracking-widest text-amber-400 mr-2">
          <Rewind size={12} /> Playback
        </div>
        {cameras.length === 0 ? (
          <span className="mono text-[10px] text-zinc-500">No cameras assigned to this account</span>
        ) : (
          cameras.map((cam) => {
            const isActive = selectedCam?.id === cam.id;
            return (
              <Button
                key={cam.id}
                data-testid={`playback-cam-${cam.channel}`}
                variant="ghost"
                onClick={() => setSelected(cam)}
                className={`rounded-none h-8 px-2 shrink-0 mono text-[11px] uppercase tracking-widest ${isActive ? "bg-amber-500/15 text-amber-300 border border-amber-500/50" : "text-zinc-400 border border-transparent hover:border-zinc-700"}`}
              >
                CH{String(cam.channel).padStart(2, "0")} · {cam.name}
              </Button>
            );
          })
        )}
      </div>

      {/* Compact calendar toolbar */}
      <div
        data-testid="playback-date-toolbar"
        className="border-b border-zinc-900 bg-black px-3 py-2 flex items-center gap-2 flex-wrap"
      >
        <div className="shrink-0 mono text-[10px] uppercase tracking-widest text-amber-400 flex items-center gap-1.5 mr-1">
          <CalendarDays size={11} /> Archive
        </div>

        {/* Prev day */}
        <button
          data-testid="playback-date-prev"
          onClick={() => shiftDay(-1)}
          disabled={!canPrev}
          title="Previous day"
          className="mono text-[10px] uppercase tracking-widest h-8 px-2 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
        >
          <ChevronLeft size={13} />
        </button>

        {/* Calendar popover */}
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              data-testid="playback-date-picker-trigger"
              className="mono text-[11px] uppercase tracking-widest h-8 px-3 border border-amber-500/60 bg-amber-500/10 text-amber-200 hover:bg-amber-500/20 flex items-center gap-2"
            >
              <CalendarDays size={13} />
              <span>{fmtLong(selectedDate)}</span>
              <span className="text-zinc-500 hidden sm:inline">·</span>
              <span className="text-zinc-400 hidden sm:inline">{fmtIsoDate(selectedDate)}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent
            data-testid="playback-date-picker-popover"
            align="start"
            className="w-auto p-0 rounded-none bg-zinc-950 border border-amber-500/40 text-zinc-100 shadow-[0_0_0_1px_rgba(0,0,0,0.6),0_20px_50px_-10px_rgba(0,0,0,0.9)]"
          >
            <div className="px-3 pt-3 pb-1 mono text-[9px] uppercase tracking-widest text-amber-400 flex items-center justify-between">
              <span>Select archive date</span>
              <span className="text-zinc-500">last 35 days</span>
            </div>
            <CalendarPicker
              mode="single"
              selected={selectedDate}
              onSelect={(d) => {
                if (!d) return;
                setSelectedDate(startOfDay(d));
                setPickerOpen(false);
              }}
              disabled={{ before: oldest, after: today }}
              defaultMonth={selectedDate}
              showOutsideDays={false}
              className="text-zinc-100"
              classNames={{
                caption_label: "text-sm font-medium text-amber-300 mono uppercase tracking-widest",
                nav_button:
                  "h-7 w-7 bg-transparent border border-zinc-800 hover:border-amber-500/50 text-zinc-300 hover:text-amber-300 rounded-none p-0",
                head_cell:
                  "text-amber-400/60 rounded-none w-8 font-normal text-[0.7rem] mono uppercase tracking-widest",
                cell:
                  "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-transparent rounded-none",
                day:
                  "h-8 w-8 p-0 font-normal rounded-none border border-transparent text-zinc-300 hover:border-amber-500/40 hover:text-amber-200 mono text-[11px]",
                day_selected:
                  "bg-amber-500 text-black hover:bg-amber-400 hover:text-black focus:bg-amber-500 focus:text-black border-amber-400",
                day_today: "border-amber-500/60 text-amber-300",
                day_disabled: "text-zinc-700 opacity-40 cursor-not-allowed",
                day_outside: "invisible",
              }}
            />
            <div className="px-3 pb-3 pt-1 flex items-center gap-2 border-t border-zinc-900">
              <button
                data-testid="playback-quick-today"
                onClick={() => { setSelectedDate(today); setPickerOpen(false); }}
                className={`mono text-[10px] uppercase tracking-widest h-7 px-2 border ${isToday ? "bg-amber-500 text-black border-amber-400" : "border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300"}`}
              >Today</button>
              <button
                data-testid="playback-quick-yesterday"
                onClick={() => { setSelectedDate(yesterday); setPickerOpen(false); }}
                className={`mono text-[10px] uppercase tracking-widest h-7 px-2 border ${isYesterday ? "bg-amber-500 text-black border-amber-400" : "border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300"}`}
              >Yesterday</button>
              <div className="flex-1" />
              <span className="mono text-[9px] text-zinc-600">{fmtIsoDate(selectedDate)}</span>
            </div>
          </PopoverContent>
        </Popover>

        {/* Next day */}
        <button
          data-testid="playback-date-next"
          onClick={() => shiftDay(1)}
          disabled={!canNext}
          title="Next day"
          className="mono text-[10px] uppercase tracking-widest h-8 px-2 border border-zinc-800 text-zinc-400 hover:border-amber-500/40 hover:text-amber-300 disabled:opacity-30 disabled:cursor-not-allowed flex items-center"
        >
          <ChevronRight size={13} />
        </button>

        {/* Jump to today shortcut */}
        {!isToday && (
          <button
            data-testid="playback-jump-today"
            onClick={() => setSelectedDate(today)}
            className="mono text-[10px] uppercase tracking-widest h-8 px-2 border border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300"
          >Jump to today</button>
        )}

        <span className="mono text-[9px] text-zinc-600 pl-2 shrink-0 hidden md:inline ml-auto">
          35-day archive · loops the day-of-week footage assigned by NVR
        </span>
      </div>

      {/* Player */}
      <div className="flex-1 min-h-0 flex" data-testid="playback-stage">
        {selectedCam ? (
          <div className="flex-1 min-h-0 max-w-[1400px] mx-auto w-full flex p-2 sm:p-4">
            <VideoTile
              key={`${selectedCam.id}-${fmtIsoDate(selectedDate)}`}
              camera={selectedCam}
              expanded
              playbackMode
              playbackDate={selectedDate}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center mono text-[11px] uppercase tracking-widest text-zinc-600">
            {cameras.length === 0 ? "No cameras available" : "Pick a camera above"}
          </div>
        )}
      </div>

      <MobileBottomNav active="playback" />
    </div>
  );
}
